"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "tw-inline-flex tw-items-center tw-cursor-pointer tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-font-medium tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-1 focus-visible:tw-ring-ring disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg]:tw-size-4 [&_svg]:tw-shrink-0",
  {
    variants: {
      variant: {
        default: "tw-bg-primary tw-text-primary-foreground hover:tw-bg-primary/90",
        destructive:
          "tw-bg-destructive tw-text-primary-foreground hover:tw-bg-destructive/90",
        cool: "dark:tw-inset-shadow-2xs dark:tw-inset-shadow-white/10 tw-bg-linear-to-t tw-border tw-border-b-2 tw-border-zinc-950/40 tw-from-primary tw-to-primary/85 tw-shadow-md tw-shadow-primary/20 tw-ring-1 tw-ring-inset tw-ring-white/25 tw-transition-[filter] tw-duration-200 hover:tw-brightness-110 active:tw-brightness-90 dark:tw-border-x-0 tw-text-primary-foreground dark:tw-text-primary-foreground dark:tw-border-t-0 dark:tw-border-primary/50 dark:tw-ring-white/5",
        outline:
          "tw-border tw-border-input tw-bg-background hover:tw-bg-accent hover:tw-text-accent-foreground",
        secondary:
          "tw-bg-secondary tw-text-secondary-foreground hover:tw-bg-secondary/80",
        ghost: "hover:tw-bg-accent hover:tw-text-accent-foreground",
        link: "tw-text-primary tw-underline-offset-4 hover:tw-underline",
      },
      size: {
        default: "tw-h-9 tw-px-4 tw-py-2",
        sm: "tw-h-8 tw-rounded-md tw-px-3 tw-text-xs",
        lg: "tw-h-10 tw-rounded-md tw-px-8",
        icon: "tw-h-9 tw-w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

const liquidButtonVariants = cva(
  "tw-inline-flex tw-items-center tw-transition-colors tw-justify-center tw-cursor-pointer tw-gap-2 tw-whitespace-nowrap tw-rounded-md tw-text-sm tw-font-medium tw-transition-[color,box-shadow] disabled:tw-pointer-events-none disabled:tw-opacity-50 [&_svg]:tw-pointer-events-none [&_svg:not([class*='size-'])]:tw-size-4 tw-shrink-0 [&_svg]:tw-shrink-0 tw-outline-none focus-visible:tw-border-ring focus-visible:tw-ring-ring/50 focus-visible:tw-ring-[3px] aria-invalid:tw-ring-destructive/20 dark:aria-invalid:tw-ring-destructive/40 aria-invalid:tw-border-destructive",
  {
    variants: {
      variant: {
        default: "tw-bg-transparent hover:tw-scale-105 tw-duration-300 tw-transition tw-text-primary",
        destructive:
          "tw-bg-destructive tw-text-white hover:tw-bg-destructive/90 focus-visible:tw-ring-destructive/20 dark:focus-visible:tw-ring-destructive/40",
        outline:
          "tw-border tw-border-input tw-bg-background hover:tw-bg-accent hover:tw-text-accent-foreground",
        secondary:
          "tw-bg-secondary tw-text-secondary-foreground hover:tw-bg-secondary/80",
        ghost: "hover:tw-bg-accent hover:tw-text-accent-foreground",
        link: "tw-text-primary tw-underline-offset-4 hover:tw-underline",
      },
      size: {
        default: "tw-h-9 tw-px-4 tw-py-2 has-[>svg]:tw-px-3",
        sm: "tw-h-8 tw-text-xs tw-gap-1.5 tw-px-4 has-[>svg]:tw-px-4",
        lg: "tw-h-10 tw-rounded-md tw-px-6 has-[>svg]:tw-px-4",
        xl: "tw-h-12 tw-rounded-md tw-px-8 has-[>svg]:tw-px-6",
        xxl: "tw-h-14 tw-rounded-md tw-px-10 has-[>svg]:tw-px-8",
        icon: "tw-size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "xxl",
    },
  }
)

function LiquidButton({
  className,
  variant,
  size,
  asChild = false,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof liquidButtonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <>
      <Comp
        data-slot="button"
        className={cn(
          "tw-relative",
          liquidButtonVariants({ variant, size, className })
        )}
        {...props}
      >
        <div className="tw-absolute tw-top-0 tw-left-0 tw-z-0 tw-h-full tw-w-full tw-rounded-full 
            tw-shadow-[0_0_6px_rgba(0,0,0,0.03),0_2px_6px_rgba(0,0,0,0.08),inset_3px_3px_0.5px_-3px_rgba(0,0,0,0.9),inset_-3px_-3px_0.5px_-3px_rgba(0,0,0,0.85),inset_1px_1px_1px_-0.5px_rgba(0,0,0,0.6),inset_-1px_-1px_1px_-0.5px_rgba(0,0,0,0.6),inset_0_0_6px_6px_rgba(0,0,0,0.12),inset_0_0_2px_2px_rgba(0,0,0,0.06),0_0_12px_rgba(255,255,255,0.15)] 
        tw-transition-all 
        dark:tw-shadow-[0_0_8px_rgba(0,0,0,0.03),0_2px_6px_rgba(0,0,0,0.08),inset_3px_3px_0.5px_-3.5px_rgba(255,255,255,0.09),inset_-3px_-3px_0.5px_-3.5px_rgba(255,255,255,0.85),inset_1px_1px_1px_-0.5px_rgba(255,255,255,0.6),inset_-1px_-1px_1px_-0.5px_rgba(255,255,255,0.6),inset_0_0_6px_6px_rgba(255,255,255,0.12),inset_0_0_2px_2px_rgba(255,255,255,0.06),0_0_12px_rgba(0,0,0,0.15)]" />
        <div
          className="tw-absolute tw-top-0 tw-left-0 tw-isolate -tw-z-10 tw-h-full tw-w-full tw-overflow-hidden tw-rounded-md"
          style={{ backdropFilter: 'url("#container-glass")' }}
        />

        <div className="tw-pointer-events-none tw-z-10">
          {children}
        </div>
        <GlassFilter />
      </Comp>
    </>
  )
}

function GlassFilter() {
  return (
    <svg className="tw-hidden">
      <defs>
        <filter
          id="container-glass"
          x="0%"
          y="0%"
          width="100%"
          height="100%"
          colorInterpolationFilters="sRGB"
        >
          {/* Generate turbulent noise for distortion */}
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.05 0.05"
            numOctaves="1"
            seed="1"
            result="turbulence"
          />

          {/* Blur the turbulence pattern slightly */}
          <feGaussianBlur in="turbulence" stdDeviation="2" result="blurredNoise" />

          {/* Displace the source graphic with the noise */}
          <feDisplacementMap
            in="SourceGraphic"
            in2="blurredNoise"
            scale="70"
            xChannelSelector="R"
            yChannelSelector="B"
            result="displaced"
          />

          {/* Apply overall blur on the final result */}
          <feGaussianBlur in="displaced" stdDeviation="4" result="finalBlur" />

          {/* Output the result */}
          <feComposite in="finalBlur" in2="finalBlur" operator="over" />
        </filter>
      </defs>
    </svg>
  );
}

type ColorVariant =
  | "default"
  | "primary"
  | "success"
  | "error"
  | "gold"
  | "bronze";
 
interface MetalButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ColorVariant;
}
 
const colorVariants: Record<
  ColorVariant,
  {
    outer: string;
    inner: string;
    button: string;
    textColor: string;
    textShadow: string;
  }
> = {
  default: {
    outer: "tw-bg-gradient-to-b tw-from-[#000] tw-to-[#A0A0A0]",
    inner: "tw-bg-gradient-to-b tw-from-[#FAFAFA] tw-via-[#3E3E3E] tw-to-[#E5E5E5]",
    button: "tw-bg-gradient-to-b tw-from-[#B9B9B9] tw-to-[#969696]",
    textColor: "tw-text-white",
    textShadow: "[text-shadow:_0_-1px_0_rgb(80_80_80_/_100%)]",
  },
  primary: {
    outer: "tw-bg-gradient-to-b tw-from-[#000] tw-to-[#A0A0A0]",
    inner: "tw-bg-gradient-to-b tw-from-primary tw-via-secondary tw-to-muted",
    button: "tw-bg-gradient-to-b tw-from-primary tw-to-primary/40",
    textColor: "tw-text-white",
    textShadow: "[text-shadow:_0_-1px_0_rgb(30_58_138_/_100%)]",
  },
  success: {
    outer: "tw-bg-gradient-to-b tw-from-[#005A43] tw-to-[#7CCB9B]",
    inner: "tw-bg-gradient-to-b tw-from-[#E5F8F0] tw-via-[#00352F] tw-to-[#D1F0E6]",
    button: "tw-bg-gradient-to-b tw-from-[#9ADBC8] tw-to-[#3E8F7C]",
    textColor: "tw-text-[#FFF7F0]",
    textShadow: "[text-shadow:_0_-1px_0_rgb(6_78_59_/_100%)]",
  },
  error: {
    outer: "tw-bg-gradient-to-b tw-from-[#5A0000] tw-to-[#FFAEB0]",
    inner: "tw-bg-gradient-to-b tw-from-[#FFDEDE] tw-via-[#680002] tw-to-[#FFE9E9]",
    button: "tw-bg-gradient-to-b tw-from-[#F08D8F] tw-to-[#A45253]",
    textColor: "tw-text-[#FFF7F0]",
    textShadow: "[text-shadow:_0_-1px_0_rgb(146_64_14_/_100%)]",
  },
  gold: {
    outer: "tw-bg-gradient-to-b tw-from-[#917100] tw-to-[#EAD98F]",
    inner: "tw-bg-gradient-to-b tw-from-[#FFFDDD] tw-via-[#856807] tw-to-[#FFF1B3]",
    button: "tw-bg-gradient-to-b tw-from-[#FFEBA1] tw-to-[#9B873F]",
    textColor: "tw-text-[#FFFDE5]",
    textShadow: "[text-shadow:_0_-1px_0_rgb(178_140_2_/_100%)]",
  },
  bronze: {
    outer: "tw-bg-gradient-to-b tw-from-[#864813] tw-to-[#E9B486]",
    inner: "tw-bg-gradient-to-b tw-from-[#EDC5A1] tw-via-[#5F2D01] tw-to-[#FFDEC1]",
    button: "tw-bg-gradient-to-b tw-from-[#FFE3C9] tw-to-[#A36F3D]",
    textColor: "tw-text-[#FFF7F0]",
    textShadow: "[text-shadow:_0_-1px_0_rgb(124_45_18_/_100%)]",
  },
};
 
const metalButtonVariants = (
  variant: ColorVariant = "default",
  isPressed: boolean,
  isHovered: boolean,
  isTouchDevice: boolean,
) => {
  const colors = colorVariants[variant];
  const transitionStyle = "all 250ms cubic-bezier(0.1, 0.4, 0.2, 1)";
 
  return {
    wrapper: cn(
      "tw-relative tw-inline-flex tw-transform-gpu tw-rounded-md tw-p-[1.25px] tw-will-change-transform",
      colors.outer,
    ),
    wrapperStyle: {
      transform: isPressed
        ? "translateY(2.5px) scale(0.99)"
        : "translateY(0) scale(1)",
      boxShadow: isPressed
        ? "0 1px 2px rgba(0, 0, 0, 0.15)"
        : isHovered && !isTouchDevice
          ? "0 4px 12px rgba(0, 0, 0, 0.12)"
          : "0 3px 8px rgba(0, 0, 0, 0.08)",
      transition: transitionStyle,
      transformOrigin: "center center",
    },
    inner: cn(
      "tw-absolute tw-inset-[1px] tw-transform-gpu tw-rounded-lg tw-will-change-transform",
      colors.inner,
    ),
    innerStyle: {
      transition: transitionStyle,
      transformOrigin: "center center",
      filter:
        isHovered && !isPressed && !isTouchDevice ? "brightness(1.05)" : "none",
    },
    button: cn(
      "tw-relative tw-z-10 tw-m-[1px] tw-rounded-md tw-inline-flex tw-h-11 tw-transform-gpu tw-cursor-pointer tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md tw-px-6 tw-py-2 tw-text-sm tw-leading-none tw-font-semibold tw-will-change-transform tw-outline-none",
      colors.button,
      colors.textColor,
      colors.textShadow,
    ),
    buttonStyle: {
      transform: isPressed ? "scale(0.97)" : "scale(1)",
      transition: transitionStyle,
      transformOrigin: "center center",
      filter:
        isHovered && !isPressed && !isTouchDevice ? "brightness(1.02)" : "none",
    },
  };
};
 
const ShineEffect = ({ isPressed }: { isPressed: boolean }) => {
  return (
    <div
      className={cn(
        "tw-pointer-events-none tw-absolute tw-inset-0 tw-z-20 tw-overflow-hidden tw-transition-opacity tw-duration-300",
        isPressed ? "tw-opacity-20" : "tw-opacity-0",
      )}
    >
      <div className="tw-absolute tw-inset-0 tw-rounded-md tw-bg-gradient-to-r tw-from-transparent tw-via-neutral-100 tw-to-transparent" />
    </div>
  );
};
 
export const MetalButton = React.forwardRef<
  HTMLButtonElement,
  MetalButtonProps
>(({ children, className, variant = "default", ...props }, ref) => {
  const [isPressed, setIsPressed] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [isTouchDevice, setIsTouchDevice] = React.useState(false);
 
  React.useEffect(() => {
    setIsTouchDevice("ontouchstart" in window || navigator.maxTouchPoints > 0);
  }, []);
 
  const buttonText = children || "Button";
  const variants = metalButtonVariants(
    variant,
    isPressed,
    isHovered,
    isTouchDevice,
  );
 
  const handleInternalMouseDown = () => {
    setIsPressed(true);
  };
  const handleInternalMouseUp = () => {
    setIsPressed(false);
  };
  const handleInternalMouseLeave = () => {
    setIsPressed(false);
    setIsHovered(false);
  };
  const handleInternalMouseEnter = () => {
    if (!isTouchDevice) {
      setIsHovered(true);
    }
  };
  const handleInternalTouchStart = () => {
    setIsPressed(true);
  };
  const handleInternalTouchEnd = () => {
    setIsPressed(false);
  };
  const handleInternalTouchCancel = () => {
    setIsPressed(false);
  };
 
  return (
    <div className={variants.wrapper} style={variants.wrapperStyle}>
      <div className={variants.inner} style={variants.innerStyle}></div>
      <button
        ref={ref}
        className={cn(variants.button, className)}
        style={variants.buttonStyle}
        {...props}
        onMouseDown={handleInternalMouseDown}
        onMouseUp={handleInternalMouseUp}
        onMouseLeave={handleInternalMouseLeave}
        onMouseEnter={handleInternalMouseEnter}
        onTouchStart={handleInternalTouchStart}
        onTouchEnd={handleInternalTouchEnd}
        onTouchCancel={handleInternalTouchCancel}
      >
        <ShineEffect isPressed={isPressed} />
        {buttonText}
        {isHovered && !isPressed && !isTouchDevice && (
          <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-rounded-lg tw-from-transparent tw-to-white/5" />
        )}
      </button>
    </div>
  );
});
 
MetalButton.displayName = "MetalButton";

export { Button, buttonVariants, liquidButtonVariants, LiquidButton }